'use client'

import { useEffect, useRef, useState } from 'react'
import { useLocale } from 'next-intl'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { usePathname, useRouter, type AvailableLocales } from '@onclicks/sharedmobilei18n/routing'
import { languages, localeToLangue } from '@onclicks/sharedmobilecommon/utils/languages'
import { useAuth } from '@/kmsApp/providers/keycloakServiceProvider'
import { useKmsService } from '@/kmsApp/providers/kms-service-provider'

export const LanguagePicker = ({ className }: { className?: string }) => {
  const currentLocale = useLocale()
  const pathname = usePathname()
  const router = useRouter()
  const { authenticated } = useAuth()
  const { userService } = useKmsService()
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = async (locale: AvailableLocales) => {
    setIsOpen(false)
    if (locale === currentLocale) return

    // Utilisateur connecté : on synchronise le backend, en best-effort — on
    // change quand même la langue localement même si l'appel échoue. On force
    // ensuite le renouvellement du token Keycloak : sans ça, le cookie garde
    // l'ancien claim `locale`, et la redirection automatique de proxy.ts (qui
    // se fie à ce claim) nous ramènerait aussitôt vers l'ancienne langue.
    if (authenticated) {
      try {
        await userService.updateMyLanguage(localeToLangue(locale))
        await fetch('/api/keycloak/refresh', { method: 'POST', cache: 'no-store' })
      } catch (error) {
        console.error('Impossible de synchroniser la langue avec le serveur :', error)
      }
    }

    router.replace(pathname, { locale })
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 hover:font-bold"
      >
        <span className="uppercase">{currentLocale}</span>
        <ChevronDownIcon
          width={14}
          height={14}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div
          className="absolute flex flex-col right-0 mt-2 w-44 bg-black border border-white/10 rounded-md shadow-xl z-[60] py-2"
          ref={dropdownRef}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleSelect(lang.code)}
              className={`w-full text-left px-6 py-2 text-white hover:bg-white/10 ${
                lang.code === currentLocale ? 'font-bold' : ''
              }`}
            >
              {lang.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
